import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Users, 
  Plus, 
  Trash2, 
  Crown,
  UserPlus,
  Mail,
  Heart
} from 'lucide-react';
import { createFamily, getFamily, getFamilyMembers, addFamilyMember, removeFamilyMember } from '@/services/api';
import { toast } from 'sonner';

const relationships = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'other', label: 'Other' }
];

const relationshipColors = {
  admin: 'bg-amber-100 text-amber-700',
  spouse: 'bg-pink-100 text-pink-700',
  child: 'bg-blue-100 text-blue-700',
  parent: 'bg-purple-100 text-purple-700',
  sibling: 'bg-green-100 text-green-700',
  grandparent: 'bg-orange-100 text-orange-700',
  other: 'bg-slate-100 text-slate-700'
};

export default function FamilyPage() {
  const { user, refreshUser } = useAuth();
  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Form states
  const [familyName, setFamilyName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberRelationship, setMemberRelationship] = useState('');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    loadFamilyData();
  }, []);

  const loadFamilyData = async () => {
    try {
      const [familyData, membersData] = await Promise.all([
        getFamily(),
        getFamilyMembers()
      ]);
      setFamily(familyData);
      setMembers(membersData);
    } catch (error) {
      console.error('Error loading family data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      toast.error('Please enter a family name');
      return;
    }

    setCreating(true);
    try {
      await createFamily(familyName);
      toast.success('Family created successfully!');
      setCreateDialogOpen(false);
      setFamilyName('');
      await refreshUser();
      await loadFamilyData();
    } catch (error) {
      console.error('Create family error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create family');
    } finally {
      setCreating(false);
    }
  };

  const handleAddMember = async () => {
    if (!memberEmail.trim() || !memberName.trim() || !memberRelationship) {
      toast.error('Please fill in all fields');
      return;
    }

    setAdding(true);
    try {
      await addFamilyMember(memberEmail, memberName, memberRelationship);
      toast.success('Family member added successfully!');
      setAddDialogOpen(false);
      setMemberEmail('');
      setMemberName('');
      setMemberRelationship('');
      await loadFamilyData();
    } catch (error) {
      console.error('Add member error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the family?`)) return;

    try {
      await removeFamilyMember(memberId);
      toast.success('Member removed successfully');
      await loadFamilyData();
    } catch (error) {
      console.error('Remove member error:', error);
      toast.error('Failed to remove member');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-64 rounded" />
          <div className="skeleton h-48 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="family-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Family Management
            </h1>
            <p className="text-slate-600 mt-1">
              {family ? `Manage ${family.name} family members` : 'Create a family to manage health records together'}
            </p>
          </div>
          
          {family && user?.is_family_admin && (
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="add-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Family Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="memberName">Full Name</Label>
                    <Input
                      id="memberName"
                      placeholder="Enter member's name"
                      value={memberName}
                      onChange={(e) => setMemberName(e.target.value)}
                      data-testid="member-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="memberEmail">Email Address</Label>
                    <Input
                      id="memberEmail"
                      type="email"
                      placeholder="Enter member's email"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      data-testid="member-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="relationship">Relationship</Label>
                    <Select value={memberRelationship} onValueChange={setMemberRelationship}>
                      <SelectTrigger id="relationship" data-testid="relationship-select">
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {relationships.map((rel) => (
                          <SelectItem key={rel.value} value={rel.value}>{rel.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                    onClick={handleAddMember}
                    disabled={adding}
                    data-testid="confirm-add-member-btn"
                  >
                    {adding ? 'Adding...' : 'Add Member'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {!family ? (
          // No Family - Create One
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="py-12">
              <div className="text-center max-w-md mx-auto">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-blue-900" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Create Your Family Group
                </h2>
                <p className="text-slate-600 mb-6">
                  Create a family group to manage health records for your loved ones in one place.
                </p>
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-8 py-5 btn-active" data-testid="create-family-btn">
                      <Plus className="w-5 h-5 mr-2" />
                      Create Family
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create Family Group</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="familyName">Family Name</Label>
                        <Input
                          id="familyName"
                          placeholder="e.g., The Smith Family"
                          value={familyName}
                          onChange={(e) => setFamilyName(e.target.value)}
                          data-testid="family-name-input"
                        />
                      </div>
                      <Button
                        className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                        onClick={handleCreateFamily}
                        disabled={creating}
                        data-testid="confirm-create-family-btn"
                      >
                        {creating ? 'Creating...' : 'Create Family'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Family Exists - Show Members
          <>
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{family.name}</CardTitle>
                    <CardDescription>
                      {members.length} member{members.length !== 1 ? 's' : ''} • Created {new Date(family.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.map((member, index) => (
                <Card 
                  key={member.user_id} 
                  className="bg-white border-slate-200 shadow-sm family-member-card animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center">
                          <span className="text-xl font-semibold text-slate-700">
                            {member.full_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{member.full_name}</h3>
                            {member.relationship === 'admin' && (
                              <Crown className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-sm text-slate-500 truncate max-w-[150px]">{member.email}</span>
                          </div>
                          <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full ${relationshipColors[member.relationship] || relationshipColors.other}`}>
                            {member.relationship.charAt(0).toUpperCase() + member.relationship.slice(1)}
                          </span>
                        </div>
                      </div>
                      {user?.is_family_admin && member.relationship !== 'admin' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveMember(member.user_id, member.full_name)}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`remove-member-${member.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
